/**
 * ErrorBoundary - React error boundary component
 * Catches errors and displays fallback UI
 */

import React, { ReactNode } from 'react';
import { Box, Button, Typography, Container } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
          <Container maxWidth="sm">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                textAlign: 'center',
                py: 4
              }}
            >
              <ErrorIcon
                sx={{
                  fontSize: 64,
                  color: '#EF4444',
                  mb: 2
                }}
              />

              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                Oops! Something went wrong
              </Typography>

              <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    p: 2,
                    mb: 3,
                    width: '100%',
                    textAlign: 'left',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
                    <strong>Error:</strong> {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', display: 'block', mt: 1 }}>
                      <strong>Stack:</strong>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = '/'}
                >
                  Go Home
                </Button>
                <Button
                  variant="contained"
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
